// PDF-Viewer mit eigener Toolbar:
//   - Download-Icon links (lädt die Datei als Attachment runter)
//   - Seiten-Indikator + ◀ ▶ rechts
//   - Canvas-Render unter der Toolbar
//
// Render-Engine: react-pdf (Wrapper um Mozilla pdf.js). Worker wird per
// Vite-URL-Import bereitgestellt — der Browser laedt ihn vom selben
// Origin nach.
//
// Robustheit:
// - Fallback-Anzeige bei Lade- oder Render-Fehlern.
// - Container-Breite wird beobachtet, sodass die Seite immer in die
//   verfuegbare Spalte passt (kein horizontales Scrollen).

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "./PdfViewer.css";

// Worker einmal pro App registrieren. Vite kopiert die Worker-Datei
// beim Build mit raus.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PdfViewerProps {
  fileUrl: string;
  downloadUrl: string;
  filename: string;
}

export function PdfViewer({ fileUrl, downloadUrl, filename }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [width, setWidth] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Wenn die Datei wechselt, zurück auf Seite 1
  useEffect(() => {
    setCurrentPage(1);
    setLoadError(null);
  }, [fileUrl]);

  // Container-Breite beobachten, damit die Seite responsive skaliert
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.floor(w));
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const goPrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const goNext = () =>
    setCurrentPage((p) => (pageCount ? Math.min(pageCount, p + 1) : p));

  return (
    <div className="bmd-pdf">
      <div className="bmd-pdf-toolbar">
        <a
          className="bmd-pdf-tool"
          href={downloadUrl}
          download={filename}
          title={`${filename} herunterladen`}
          aria-label="Datei herunterladen"
        >
          <DownloadIcon />
        </a>

        <div className="bmd-pdf-pager">
          <button
            type="button"
            className="bmd-pdf-tool"
            onClick={goPrev}
            disabled={currentPage <= 1}
            aria-label="Vorherige Seite"
          >
            <ChevronLeftIcon />
          </button>
          <span className="bmd-pdf-pageinfo">
            {pageCount === 0 ? (
              <span className="bmd-subtle">…</span>
            ) : (
              <>
                Seite <strong>{currentPage}</strong>
                <span className="bmd-subtle"> / {pageCount}</span>
              </>
            )}
          </span>
          <button
            type="button"
            className="bmd-pdf-tool"
            onClick={goNext}
            disabled={currentPage >= pageCount}
            aria-label="Nächste Seite"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      <div ref={containerRef} className="bmd-pdf-stage">
        {loadError ? (
          <div className="bmd-pdf-error">
            Datei konnte nicht angezeigt werden: {loadError}
          </div>
        ) : (
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages }) => setPageCount(numPages)}
            onLoadError={(err) => setLoadError(err.message)}
            loading={<div className="bmd-pdf-loading">Lade Datei…</div>}
            error={<div className="bmd-pdf-error">Fehler beim Laden.</div>}
          >
            {width && (
              <Page
                pageNumber={currentPage}
                width={width}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            )}
          </Document>
        )}
      </div>
    </div>
  );
}

// --- Inline-Icons (kein extra Icon-Lib noetig fuer drei Symbole) ---

function DownloadIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
